import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';

import { TOAST_DATA, TOAST_REF, ToastData, ToastRef } from './toast.service';

/** How long a toast stays when the caller gives no duration. */
export const DEFAULT_EDITOR_TOAST_DURATION_MS = 2000;

@Component({
    selector: 'app-toast',
    templateUrl: './toast.component.html',
    styleUrls: ['toast.component.scss'],
    animations: [
        trigger('slideIn', [
            state('in', style({ opacity: 1 })),
            transition('void => *', [
                style({ opacity: 0 }),
                animate('200ms ease-out')
            ]),
            transition('* => void', [
                animate('200ms ease-in', style({ opacity: 0 }))
            ])
        ])
    ],
    imports: [NgClass],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class ToastComponent implements OnInit, OnDestroy {
    readonly data = inject<ToastData>(TOAST_DATA);
    readonly ref = inject<ToastRef>(TOAST_REF);

    public visible: boolean = true;

    /** Every type times out; only an explicit duration of 0 stays until dismissed. */
    readonly persistent = this.getProgressDuration() <= 0;

    /** Screen readers interrupt only for errors and warnings. */
    readonly role = this.data.type === 'error' || this.data.type === 'warning' ? 'alert' : 'status';

    private readonly hovered = signal(false);
    private readonly focused = signal(false);

    /** The timer stops while the toast is hovered or has focus. */
    readonly paused = computed(() => this.hovered() || this.focused());

    private remainingMs = 0;
    private startedAt = 0;
    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        this.remainingMs = this.getProgressDuration();
        if (!this.persistent) {
            this.startTimer();
        }
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    close(): void {
        this.stopTimer();
        this.visible = false;
        // Small delay to allow animation to complete
        setTimeout(() => this.ref.close(), 300);
    }

    onPointerEnter(): void {
        this.hovered.set(true);
        this.updateTimer();
    }

    onPointerLeave(): void {
        this.hovered.set(false);
        this.updateTimer();
    }

    onFocusIn(): void {
        this.focused.set(true);
        this.updateTimer();
    }

    onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget;
        const host = event.currentTarget;
        if (next instanceof Node && host instanceof Node && host.contains(next)) return;
        this.focused.set(false);
        this.updateTimer();
    }

    private updateTimer(): void {
        if (this.persistent || !this.visible) return;
        if (this.paused()) {
            this.stopTimer();
        } else {
            this.startTimer();
        }
    }

    private startTimer(): void {
        if (this.timeoutId !== null) return;
        this.startedAt = Date.now();
        this.timeoutId = setTimeout(() => this.close(), this.remainingMs);
    }

    private stopTimer(): void {
        if (this.timeoutId === null) return;
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.remainingMs = Math.max(0, this.remainingMs - (Date.now() - this.startedAt));
    }

    private getDefaultDuration(): number {
        return DEFAULT_EDITOR_TOAST_DURATION_MS;
    }

    getToastClasses(): string {
        const baseClasses = 'toast-container';
        
        switch (this.data.type) {
            case 'success':
                return `${baseClasses} toast-success`;
            case 'error':
                return `${baseClasses} toast-error`;
            case 'warning':
                return `${baseClasses} toast-warning`;
            case 'info':
                return `${baseClasses} toast-info`;
            case 'debug':
                return `${baseClasses} toast-debug`;
            default:
                return `${baseClasses} toast-success`;
        }
    }

    getIconPath(): string {
        switch (this.data.type) {
            case 'success':
                return 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
            case 'error':
                return 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z';
            case 'warning':
                return 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z';
            case 'info':
                return 'M11.25 11.25l.041-.20a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
            case 'debug':
                return 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655-4.653a2.548 2.548 0 010-3.586l.823-.823a2.548 2.548 0 013.586 0l4.655 4.653M3.75 12l8.003-8.003a1.125 1.125 0 011.591 0l2.659 2.659a1.125 1.125 0 010 1.591L7.5 16.5';
            default:
                return 'M11.25 11.25l.041-.020a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
        }
    }

    getProgressDuration(): number {
        return this.data.duration ?? this.getDefaultDuration();
    }
}